<template>
  <!-- Adapted from VideoSurvFront/src/components/home/HomeToolbar.vue. -->
  <header class="home-toolbar">
    <div class="home-toolbar__left">
      <p v-if="siteLabel" class="home-toolbar__site-label">{{ siteLabel }}</p>
      <label class="sr-only">Группа камер</label>
      <div class="home-toolbar__select-wrap">
        <button
          type="button"
          class="home-toolbar__select home-toolbar__group-btn"
          :class="{ 'home-toolbar__group-btn--active': groupPanelOpen }"
          @click="groupPanelOpen = !groupPanelOpen"
        >
          <span class="home-toolbar__group-label">{{ groupButtonLabel }}</span>
        </button>
        <svg class="home-toolbar__chevron" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>

        <div v-if="groupPanelOpen" class="home-toolbar__group-panel">
          <p class="home-toolbar__group-hint">Отметьте одну или несколько групп</p>
          <label class="home-toolbar__group-option home-toolbar__group-option--all">
            <input type="checkbox" :checked="allGroupsChecked" @change="toggleAllGroups" />
            <span>{{ $loc('menu.allCameras') }}</span>
          </label>
          <label
            v-for="option in assignableGroups"
            :key="option.id"
            class="home-toolbar__group-option"
          >
            <input
              type="checkbox"
              :checked="filterGroupIds.includes(option.id)"
              @change="toggleFilterGroup(option.id, ($event.target as HTMLInputElement).checked)"
            />
            <span>{{ option.name }}</span>
          </label>
        </div>
      </div>
    </div>

    <div class="home-toolbar__right">
      <div
        v-if="viewMode === 'tile'"
        class="home-toolbar__columns"
        role="group"
        aria-label="Камер в ряду"
      >
        <label class="sr-only">Камер в ряду</label>
        <select
          class="home-toolbar__columns-select"
          aria-label="Камер в ряду"
          :value="gridColumns"
          @change="onGridColumnsChange"
        >
          <option v-for="n in 2" :key="n" :value="n" :selected="n === gridColumns">{{ n }}</option>
        </select>
      </div>

      <div class="home-toolbar__view-toggle" role="group" :aria-label="$loc('camera.viewTile')">
        <button
          type="button"
          class="home-toolbar__view-btn"
          :class="{ 'home-toolbar__view-btn--active': viewMode === 'tile' }"
          :title="$loc('camera.viewTile')"
          :aria-pressed="viewMode === 'tile'"
          @click="$emit('update:viewMode', 'tile')"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z" />
          </svg>
        </button>
        <button
          type="button"
          class="home-toolbar__view-btn"
          :class="{ 'home-toolbar__view-btn--active': viewMode === 'list' }"
          :title="$loc('camera.viewList')"
          :aria-pressed="viewMode === 'list'"
          @click="$emit('update:viewMode', 'list')"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M3 5h18v2H3V5zm0 6h18v2H3v-2zm0 6h18v2H3v-2z" />
          </svg>
        </button>
      </div>

      <button
        type="button"
        class="home-toolbar__action"
        :class="{ 'home-toolbar__action--active': selectionMode }"
        title="Выделить камеры кликом"
        @click="$emit('update:selectionMode', !selectionMode)"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path d="M9 11l3 3L22 4" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
        <span>Выбор</span>
      </button>

      <button
        v-if="selectionMode && selectionDraftCount > 0"
        type="button"
        class="home-toolbar__action home-toolbar__action--primary"
        @click="$emit('applySelection')"
      >
        Показать ({{ selectionDraftCount }})
      </button>

      <button
        v-if="selectionApplied"
        type="button"
        class="home-toolbar__action"
        @click="$emit('clearSelectionView')"
      >
        Сбросить выбор
      </button>

      <button
        type="button"
        class="home-toolbar__action"
        :class="{ 'home-toolbar__action--active': filterOpen }"
        @click="filterOpen = !filterOpen"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path d="M4 6h16M7 12h10M10 18h4" stroke-linecap="round" />
        </svg>
        <span>Фильтр</span>
      </button>

      <div v-if="filterOpen" class="home-toolbar__filter-panel">
        <label class="home-toolbar__filter-option">
          <input v-model="filterModel" type="radio" value="all" @change="emitFilter" />
          <span>Все камеры</span>
        </label>
        <label class="home-toolbar__filter-option">
          <input v-model="filterModel" type="radio" value="online" @change="emitFilter" />
          <span>Только подключённые</span>
        </label>
        <label class="home-toolbar__filter-option">
          <input v-model="filterModel" type="radio" value="offline" @change="emitFilter" />
          <span>Только отключённые</span>
        </label>
      </div>

      <button type="button" class="home-toolbar__action" @click="$emit('fullscreen')">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path
            d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"
            stroke-linecap="round"
          />
        </svg>
        <span>{{ $loc('camera.fullscreen') }}</span>
      </button>
    </div>
  </header>
</template>

<script setup lang="ts">
const ALL_CAMERAS_ID = 'all';
type Group = { id: string; name: string; system?: boolean };
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

type ViewMode = 'tile' | 'list';
type CameraFilter = 'all' | 'online' | 'offline';

const props = defineProps<{
  viewMode: ViewMode;
  groups: Group[];
  selectedGroupId: string;
  filterGroupIds: string[];
  filter: CameraFilter;
  gridColumns: number;
  selectionMode: boolean;
  selectionDraftCount: number;
  selectionApplied: boolean;
  siteLabel?: string | null;
}>();

const emit = defineEmits<{
  'update:viewMode': [ViewMode];
  'update:selectedGroupId': [string];
  'update:filterGroupIds': [string[]];
  'update:filter': [CameraFilter];
  'update:gridColumns': [number];
  'update:selectionMode': [boolean];
  applySelection: [];
  clearSelectionView: [];
  fullscreen: [];
}>();

const labels: Record<string, string> = {
  'menu.allCameras': 'Все камеры',
  'camera.viewTile': 'Плитка',
  'camera.viewList': 'Список',
  'camera.fullscreen': 'На весь экран',
};
const $loc = (key: string) => labels[key] || key;

const filterOpen = ref(false);
const groupPanelOpen = ref(false);
const filterModel = ref<CameraFilter>(props.filter);

watch(
  () => props.filter,
  (value) => {
    filterModel.value = value;
  },
);

const assignableGroups = computed(() => props.groups.filter((group) => !group.system));

const allGroupsChecked = computed(
  () => props.filterGroupIds.length === 0 || props.filterGroupIds.includes(ALL_CAMERAS_ID),
);

const groupButtonLabel = computed(() => {
  if (props.filterGroupIds.length === 0) {
    if (props.selectedGroupId === ALL_CAMERAS_ID) {
      return $loc('menu.allCameras') as string;
    }
    const group = props.groups.find((item) => item.id === props.selectedGroupId);
    return group?.name ?? ($loc('menu.allCameras') as string);
  }
  if (props.filterGroupIds.includes(ALL_CAMERAS_ID)) {
    return $loc('menu.allCameras') as string;
  }
  const names = props.filterGroupIds
    .map((id) => props.groups.find((item) => item.id === id)?.name)
    .filter((name): name is string => !!name);
  if (names.length === 1) {
    return names[0];
  }
  if (names.length > 1) {
    return names.join(', ');
  }
  return 'Группы';
});

function toggleFilterGroup(groupId: string, checked: boolean) {
  let next = [...props.filterGroupIds];
  if (checked) {
    if (!next.includes(groupId)) {
      next.push(groupId);
    }
    next = next.filter((id) => id !== ALL_CAMERAS_ID);
  } else {
    next = next.filter((id) => id !== groupId);
  }
  emit('update:filterGroupIds', next);
}

function toggleAllGroups(event: Event) {
  const checked = (event.target as HTMLInputElement).checked;
  if (checked) {
    emit('update:filterGroupIds', [ALL_CAMERAS_ID]);
    return;
  }
  emit('update:filterGroupIds', []);
}

function onGridColumnsChange(event: Event) {
  const value = Number((event.target as HTMLSelectElement).value);
  if (value >= 1 && value <= 2) {
    emit('update:gridColumns', value);
  }
}

const emitFilter = () => {
  emit('update:filter', filterModel.value);
};

function onDocumentClick(event: MouseEvent) {
  const target = event.target as HTMLElement | null;
  if (!target?.closest('.home-toolbar__right')) filterOpen.value = false;
  if (!target?.closest('.home-toolbar__select-wrap')) {
    groupPanelOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
});
</script>

<style scoped lang="scss">
@use '~/assets/styles/demo/toolbar.scss' as *;
</style>
